import React, { useState } from 'react';
import { Button, Card, Table, TablePaginationConfig, Modal, Input } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Firmware, UnusedFirmwares } from '@services/types';

import api from '@services/api';
import { toast } from 'react-toastify';
import { DeleteFirmwaresRequest } from '@openapi';

const { Column } = Table;

const UnusedFirmware: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [firmwares, setFirmwares] = useState<UnusedFirmwares[]>([]);
  const [openModal, setOpenModal] = useState<Firmware | any>();
  const [additionalData, setAdditionalData] = useState('');

  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: firmwares.length > 10,
    total: firmwares.length,
  });
  const showDeleteConfirm = async (firmware: DeleteFirmwaresRequest['firmwareIds'], text: string) => {
    try {
      if (text === 'permanently delete') {
        await api.firmwares.deleteUnusedFirmware({ firmwareIds: firmware });
        setFirmwares(firmwares.filter((el: any) => !firmware?.includes(el.id)));
        setSelectedRowKeys([]);
        setAdditionalData('');
        setOpenModal(undefined);
        toast.success('Firmwares was deleted successfully');
      } else {
        toast.error('Error in text input field');
      }
    } catch (error) {
      toast.error('Firmwares was not deleted');
    }
  };

  React.useEffect(() => {
    api.firmwares.getUnusedFirmwares().then((newFirmwares) => {
      setFirmwares(newFirmwares.elements);
      setLoading(false);
    });
  }, []);

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const onSelectChange = (newSelectedRowKeys: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  return (
    <>
      <Card
        title={`Firmware  List`}
        extra={
          <Button
            size="middle"
            danger
            disabled={selectedRowKeys.length < 1}
            type="primary"
            onClick={() => {
              setOpenModal(selectedRowKeys);
            }}
          >
            Delete
          </Button>
        }
        bodyStyle={{ padding: 0 }}
      ></Card>
      <Table
        loading={loading}
        dataSource={firmwares}
        rowSelection={rowSelection}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey={(record: Firmware) => record.id}
        scroll={{ x: 1200 }}
      >
        <Column
          title="Modification date"
          width="150px"
          align="center"
          render={(date: Firmware['modifiedAt']) => date?.toLocaleString()}
          dataIndex="modifiedAt"
        />
        <Column
          title="Compilation date"
          width="150px"
          align="center"
          render={(date: Firmware['date']) => date?.toLocaleString()}
          dataIndex="date"
        />
        <Column
          title="Device Type"
          align="center"
          render={(deviceTypes: Firmware['deviceTypes']) => {
            if (deviceTypes?.length === 0) {
              return 'Unknown';
            } else if (deviceTypes?.length === 1) {
              return deviceTypes[0];
            } else {
              return 'Multiple device types';
            }
          }}
          dataIndex="deviceTypes"
          ellipsis
        />
        <Column title="Name" align="center" dataIndex="name" />
        <Column title="Type" align="center" dataIndex="type" />
        <Column title="Version" align="center" dataIndex="version" />
        <Column
          title="Actions"
          width="130px"
          key="action"
          align="center"
          fixed="right"
          render={(text, firmware: Firmware) => <DeleteOutlined onClick={() => setOpenModal([firmware.id])} />}
        />
      </Table>
      <Modal
        title={'Are you sure delete this Firmwares?'}
        destroyOnClose={true}
        okText={'Delete'}
        okType={'danger'}
        cancelText={'Cancel'}
        onCancel={() => {
          setAdditionalData('');
          setOpenModal(undefined);
        }}
        visible={!!openModal}
        onOk={() => showDeleteConfirm(openModal, additionalData)}
      >
        <div>
          <p>
            To confirm deletion, type <span style={{ fontStyle: 'italic' }}>permanently delete</span> in the text input
            field:
          </p>
          <Input
            placeholder="permanently delete"
            onChange={(e) => {
              setAdditionalData(e.target.value);
            }}
          />
        </div>
      </Modal>
    </>
  );
};
export default React.memo(UnusedFirmware);
